import React, { useEffect, useState } from "react";
import styles from "./styles.module.scss";
import { ModalForm } from "../../../types/config";


interface FormProps {
  data?: ModalForm;
  options?: any;
  onSubmit?: (formData: any) => void;
  setPayloadValue?: (key: string, value: any) => void;
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  closeModal: any;
  setSubmitModal: any;
  sendAnalyticsData?: any;
  setConsentCheckPoints: any;
}

const PopoverForm: React.FC<FormProps> = ({
  data,
  setPayloadValue,
  onSubmit,
  options,
  closeModal,
  setSubmitModal,
  showModal,
  sendAnalyticsData,
  setConsentCheckPoints,
}) => {
  const [formData, setFormData] = useState({
    firstName: "",
    email: "",
    phone: "",
    zipCodeInput: "",
    houseType: "",
    agreedToTerms: false,
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMarketingConsentChecked, setIsMarketingConsentChecked] =
    useState(false);
  const [isThirdPartyMarketingChecked, setIsThirdPartyMarketingChecked] =
    useState(false);
  const [isThirdPartyProfilingChecked, setIsThirdPartyProfilingChecked] =
    useState(false);

  const toggleDropdown = () => {
    setIsExpanded((prev) => !prev);
  };

  useEffect(() => {
    if (isMarketingConsentChecked === true) {
      setIsThirdPartyMarketingChecked(true);
      setIsThirdPartyProfilingChecked(true);
    }
  }, [isMarketingConsentChecked]);

  useEffect(() => {
    if (!isThirdPartyMarketingChecked || !isThirdPartyProfilingChecked) {
      setIsMarketingConsentChecked(false);
    }
    if (isThirdPartyMarketingChecked && isThirdPartyProfilingChecked) {
      setIsMarketingConsentChecked(true);
    }
  }, [isThirdPartyMarketingChecked, isThirdPartyProfilingChecked]);

  useEffect(() => {
    setConsentCheckPoints?.({
      thirdPartyMarketing: isThirdPartyMarketingChecked,
      thirdPartyProfiling: isThirdPartyProfilingChecked,
    });
  }, [
    isMarketingConsentChecked,
    isThirdPartyMarketingChecked,
    isThirdPartyProfilingChecked,
  ]);

  const handleMarketingConsentChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = e.target.checked;
    setIsThirdPartyMarketingChecked(checked);
    setIsThirdPartyProfilingChecked(checked);
  };

  const handleThirdPartyMarketingChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = e.target.checked;
    setIsThirdPartyMarketingChecked(checked);
  };

  const handleThirdPartyProfilingChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = e.target.checked;
    setIsThirdPartyProfilingChecked(checked);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (e.target instanceof HTMLInputElement && e.target.type === "checkbox") {
      const isChecked = e.target.checked;
      setFormData((prevData) => ({
        ...prevData,
        [name]: isChecked,
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.firstName ||
      !formData.email ||
      !formData.phone ||
      !formData.zipCodeInput ||
      !formData.houseType ||
      !formData.agreedToTerms ||
      !isMarketingConsentChecked
    ) {
      return;
    }
    if (setPayloadValue) {
      setPayloadValue("contact[name]", formData.firstName);
      setPayloadValue("contact[email]", formData.email);
      setPayloadValue("contact[phone]", formData.phone);
      setPayloadValue("contact[zipcode]", formData.zipCodeInput);
      setPayloadValue("house_type", formData.houseType);
      setPayloadValue("agreedToTerms", formData.agreedToTerms);
    }
    if (onSubmit) {
      setSubmitModal(true);
      onSubmit(formData);
    }
    sendAnalyticsData("PopOverForm Name", "name");
    sendAnalyticsData("PopOverForm Email", "email");
    sendAnalyticsData("PopOverForm Phone", "phone");
    sendAnalyticsData("contact_form", "zipCode");
    sendAnalyticsData("PopOverForm HouseType", formData.houseType);
    sendAnalyticsData("PopOverForm Agreed to Term", formData.agreedToTerms);
    sendAnalyticsData("popover_form", "continue_button");

    closeModal();
  };

  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/;
  const match = data?.privacyConsent?.label?.match(linkRegex);

  const privacyPolicyContent = match
    ? {
        before: data?.privacyConsent?.label.split(match[0])[0],
        linkText: match[1],
        linkUrl: match[2],
        after: data?.privacyConsent?.label.split(match[0])[1],
      }
    : {
        before: data?.privacyConsent?.label,
        linkText: "",
        linkUrl: "",
        after: "",
      };

  const isSubmitDisabled =
    !formData.firstName ||
    !formData.email ||
    !formData.phone ||
    !formData.zipCodeInput ||
    !formData.houseType ||
    !formData.agreedToTerms ||
    !isMarketingConsentChecked;

  return (
    <div
      id="modal"
      className={styles.modalOverlay}
      onClick={closeModal}
      style={{ display: showModal ? "flex" : "none" }}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeModalButton} onClick={closeModal}>
          &times;
        </button>
        {data?.formTitle && (
          <>
            <div className={styles.title}>
              {data?.formTitle?.text || "Form Title"}
            </div>
            <div className={styles.subtitle}>
              {data?.formTitle?.description || "Form Description"}
            </div>
          </>
        )}
        <form onSubmit={handleSubmit}>
          {data?.fields.name && (
            <>
              <div className={styles.formGroup}>
                <input
                  type="text"
                  id="firstName"
                  placeholder={data?.fields?.name?.placeholder || "First Name"}
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </div>
            </>
          )}
          {data?.fields.email && (
            <div className={styles.formGroup}>
              <input
                type="email"
                placeholder={data?.fields?.email?.placeholder || "Email"}
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
          )}
          {data?.fields.phone && (
            <div className={styles.formGroup}>
              <input
                type="text"
                placeholder={data?.fields?.phone?.placeholder || "Phone"}
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </div>
          )}
          {data?.fields.zipCodeInput && (
            <>
              <div className={styles.formGroup}>
                <input
                  type="text"
                  id="zipCodeInput"
                  placeholder={
                    data?.fields?.zipCodeInput.placeholder || "Zip Code"
                  }
                  name="zipCodeInput"
                  value={formData.zipCodeInput}
                  onChange={handleChange}
                  maxLength={5}
                  required
                />
              </div>
            </>
          )}
          {data?.fields.housingType && (
            <div className={styles.houseTypeField}>
              <select
                id="houseType"
                name="houseType"
                onChange={handleChange}
                className={`${styles.inputField} ${styles.customSelect}`}
                value={formData.houseType}
                required>
                <option value="" disabled>
                  {data?.fields?.housingType?.placeholder || "Select Type"}
                </option>
                {options?.map((option: any, index: number) => (
                  <option key={index} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          {data?.privacyConsent && (
            <div className={`${styles.lastGroup} ${styles.formGroup}`}>
              <input
                type="checkbox"
                name="agreedToTerms"
                checked={formData.agreedToTerms}
                onChange={handleChange}
                className={styles.checkBox}
                required
              />
              <label>
                {data?.privacyConsent?.label && (
                  <div>
                    {privacyPolicyContent.before}
                    <a
                      className={styles.linkAttached}
                      href={privacyPolicyContent.linkUrl}
                      target="_blank"
                      rel="noopener noreferrer">
                      {privacyPolicyContent.linkText}
                    </a>
                    {privacyPolicyContent.after}
                  </div>
                )}
              </label>
            </div>
          )}
          {data?.privacyConsent && (
            <div
              className={`${styles.lastGroup} ${styles.formGroup} ${styles.checkGroup}`}>
              <div className={styles.checkboxContainer}>
                <input
                  type="checkbox"
                  name="agreedToTerms"
                  onChange={handleMarketingConsentChange}
                  checked={
                    isThirdPartyMarketingChecked && isThirdPartyProfilingChecked
                  }
                  className={styles.checkBox}
                  required
                />
                <label
                  onClick={toggleDropdown}
                  className={styles.labelWithIcon}>
                  {data?.privacyConsent?.label && (
                    <div>{data?.marketingTitle}</div>
                  )}
                  <span className={styles.dropdownIcon}>
                    {isExpanded ? "−" : "+"}
                  </span>
                </label>
              </div>

              {isExpanded && (
                <div className={styles.dropdownContent}>
                  <div className={styles.checkpoint}>
                    <input
                      type="checkbox"
                      name="checkpoint1"
                      checked={isThirdPartyMarketingChecked}
                      onChange={handleThirdPartyMarketingChange}
                      className={styles.checkBox}
                    />
                    <div>
                      <div className={styles.infoBox}>
                        {data?.thirdPartyMarketing?.title}
                      </div>
                      <label>{data?.thirdPartyMarketing?.description}</label>
                    </div>
                  </div>
                  <div className={styles.checkpoint}>
                    <input
                      type="checkbox"
                      name="checkpoint2"
                      checked={isThirdPartyProfilingChecked}
                      onChange={handleThirdPartyProfilingChange}
                      className={styles.checkBox}
                    />
                    <div>
                      <div className={styles.infoBox}>
                        {data?.thirdPartyProfiling?.title}
                      </div>
                      <label>{data?.thirdPartyProfiling?.description}</label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          <button
            type="submit"
            className={`${styles.submitButton} ${
              isSubmitDisabled ? styles.disabled : ""
            }`}
            disabled={isSubmitDisabled}>
            {data?.button?.text || "Submit"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PopoverForm;
